import { useState } from "react";
import type { ConfecamarasRecord, ProgresoConsulta } from "@/api/confecamaras";

interface Props {
  resultados: ProgresoConsulta[];
}

const labelEstado = (status: string) => {
  switch (status) {
    case "ok":
      return "OK";
    case "not_found":
      return "Sin datos";
    case "error":
      return "Error";
    default:
      return status;
  }
};

const colorEstado = (status: string) => {
  switch (status) {
    case "ok":
      return "bg-emerald-100 text-emerald-800";
    case "not_found":
      return "bg-amber-100 text-amber-800";
    case "error":
      return "bg-red-100 text-red-800";
    default:
      return "bg-slate-100 text-slate-800";
  }
};

const titleCase = (s: string | null | undefined) =>
  (s ?? "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

export const ConsultaResultadosTable = ({ resultados }: Props) => {
  const [expandido, setExpandido] = useState<string | null>(null);

  if (resultados.length === 0) {
    return (
      <div className="rounded-lg bg-white p-6 text-center text-sm text-ink-secondary shadow-card">
        Los resultados aparecerán aquí a medida que se consulten.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow-card">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-ink-secondary">
            <tr>
              <th className="px-4 py-3">NIT</th>
              <th className="px-4 py-3">Razón social</th>
              <th className="px-4 py-3">Cámara</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3">Registros</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {resultados.map((r) => (
              <ResultadosFila
                key={r.nit}
                resultado={r}
                expandido={expandido === r.nit}
                onToggle={() =>
                  setExpandido((prev) => (prev === r.nit ? null : r.nit))
                }
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface FilaProps {
  resultado: ProgresoConsulta;
  expandido: boolean;
  onToggle: () => void;
}

const ResultadosFila = ({ resultado, expandido, onToggle }: FilaProps) => {
  const primary = resultado.records[0];
  const registros: ConfecamarasRecord[] = resultado.records;

  return (
    <>
      <tr className="hover:bg-slate-50">
        <td className="px-4 py-3">
          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">
            {resultado.nit}
          </code>
        </td>
        <td className="px-4 py-3 uppercas text-ink-primary">
          {titleCase(resultado.razon_social) || "—"}
        </td>
        <td className="px-4 py-3 uppercas text-ink-secondary">
          {titleCase(resultado.camara_comercio) || "—"}
        </td>
        <td className="px-4 py-3">
          <span
            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${colorEstado(resultado.status)}`}
          >
            {labelEstado(resultado.status)}
          </span>
        </td>
        <td className="px-4 py-3 text-ink-secondary">
          {resultado.cantidad_registros}
        </td>
        <td className="px-4 py-3 text-right">
          <button
            type="button"
            onClick={onToggle}
            disabled={registros.length === 0}
            className="rounded-md px-3 py-1 text-xs font-medium text-accent-blue hover:bg-slate-100 disabled:opacity-50"
          >
            {expandido ? "Ocultar" : "Ver matrículas"}
          </button>
        </td>
      </tr>
      {expandido ? (
        <tr className="bg-slate-50">
          <td colSpan={6} className="px-4 py-3">
            {registros.length === 0 ? (
              <p className="text-sm text-ink-secondary">
                {resultado.error ?? "Sin registros devueltos por la API."}
              </p>
            ) : (
              <div className="space-y-3">
                {registros.map((reg, idx) => (
                  <div
                    key={idx}
                    className="rounded-md border border-slate-200 bg-white p-3"
                  >
                    <p className="text-sm font-medium text-ink-primary">
                      {titleCase(reg.razon_social) || "(sin razón social)"}
                    </p>
                    <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1 text-xs text-ink-secondary sm:grid-cols-3">
                      <div>
                        <dt className="font-medium text-ink-primary">Cámara</dt>
                        <dd className="uppercas">{titleCase(reg.camara_comercio)}</dd>
                      </div>
                      <div>
                        <dt className="font-medium text-ink-primary">Matrícula</dt>
                        <dd>{reg.matricula ?? "—"}</dd>
                      </div>
                      <div>
                        <dt className="font-medium text-ink-primary">Estado</dt>
                        <dd>{titleCase(reg.estado_matricula)}</dd>
                      </div>
                      <div>
                        <dt className="font-medium text-ink-primary">Tipo sociedad</dt>
                        <dd className="uppercas">{titleCase(reg.tipo_sociedad)}</dd>
                      </div>
                      <div>
                        <dt className="font-medium text-ink-primary">Organización</dt>
                        <dd className="uppercas">{titleCase(reg.organizacion_juridica)}</dd>
                      </div>
                      <div>
                        <dt className="font-medium text-ink-primary">CIIU</dt>
                        <dd>{reg.cod_ciiu_act_econ_pri ?? "—"}</dd>
                      </div>
                      <div>
                        <dt className="font-medium text-ink-primary">F. matrícula</dt>
                        <dd>{reg.fecha_matricula ?? "—"}</dd>
                      </div>
                      <div>
                        <dt className="font-medium text-ink-primary">F. vigencia</dt>
                        <dd>{reg.fecha_vigencia ?? "—"}</dd>
                      </div>
                      <div>
                        <dt className="font-medium text-ink-primary">DV</dt>
                        <dd>{reg.digito_verificacion ?? "—"}</dd>
                      </div>
                    </dl>
                  </div>
                ))}
              </div>
            )}
          </td>
        </tr>
      ) : null}
      {!primary && resultado.status === "ok" ? null : null}
    </>
  );
};
